import React, {ComponentType, useEffect} from "react";
import {connect} from "react-redux";
import {useNavigate, useLocation} from "react-router-dom";
import {RoleNameEnum} from "../../utils/types/common";
import {UserDto} from "../../utils/types/user";

import {select, dispatch, AppState} from "../../store";
import {createRouteGuard} from "../../routers/createRouteGuard";
import Alert from "react-s-alert";

// Regex patterns for ORGANIZATION_IT_ADMIN allowed routes
export const SSO_CONFIG_PATTERN =
  /^\/admin\/organizations\/[^/]+\/sso-configuration(\/.*)?$/;
export const SSO_LOGIN_PATTERN = /^\/admin\/sso(-login-form|\/login)$/;

/**
 * Checks if a path is allowed for ORGANIZATION_IT_ADMIN users.
 */
export const isAllowedPathForItAdmin = (pathname: string): boolean => {
  return SSO_CONFIG_PATTERN.test(pathname) || SSO_LOGIN_PATTERN.test(pathname);
};

/**
 * Gets the SSO configuration path for a given organization.
 */
export const getSsoConfigPath = (organizationId: string): string => {
  return `/admin/organizations/${organizationId}/sso-configuration`;
};


const createAdminHasRoleGuard = (...roles: ReadonlyArray<RoleNameEnum>) =>
  createRouteGuard({
    authenticatedSelector: state =>
      roles.includes(select.adminSession.userRoleName(state)),
    redirectPath: "/admin/dashboard",
  });

export const AdminIsNotResideAdminGuard = createAdminHasRoleGuard(
  RoleNameEnum.CLERK,
  RoleNameEnum.FACILITY_ADMIN,
  RoleNameEnum.ADMISSIONS_DIRECTOR,
  RoleNameEnum.REGIONAL_ADMIN,
  RoleNameEnum.ORGANIZATION_ADMIN,
);

export const AdminIsResideAdminGuard = createAdminHasRoleGuard(
  RoleNameEnum.RESIDE_ADMIN,
);

export const AdminIsNotClerkGuard = createAdminHasRoleGuard(
  RoleNameEnum.FACILITY_ADMIN,
  RoleNameEnum.ADMISSIONS_DIRECTOR,
  RoleNameEnum.REGIONAL_ADMIN,
  RoleNameEnum.ORGANIZATION_IT_ADMIN,
  RoleNameEnum.ORGANIZATION_ADMIN,
  RoleNameEnum.RESIDE_ADMIN,
);

export const AdminIsOrganizationAdminGuard = createAdminHasRoleGuard(
  RoleNameEnum.RESIDE_ADMIN,
  RoleNameEnum.ORGANIZATION_ADMIN,
);

/**
 * Guard that restricts ORGANIZATION_IT_ADMIN to only access SSO-related routes:
 * - /admin/organizations/:organizationId/sso-configuration (and all subroutes)
 * - /admin/sso-login-form
 * - /admin/sso/login
 */
export function AdminIsOrganizationItAdminRouteGuard<OwnProps>(
  Component: ComponentType<OwnProps>,
) {
  const mapState = (state: AppState) => ({
    userRole: select.adminSession.userRoleName(state),
    user: select.adminSession.user(state),
  });

  function Redirect({
    userRole,
    user,
    ...props
  }: OwnProps & {userRole: RoleNameEnum | null; user: UserDto}) {
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
      // Return early if the user is not an ORGANIZATION_IT_ADMIN
      if (userRole !== RoleNameEnum.ORGANIZATION_IT_ADMIN) return;

      if (!isAllowedPathForItAdmin(location.pathname)) {
        const organizationId = user.organization?.id;

        if (organizationId) {
          navigate(getSsoConfigPath(organizationId), {replace: true});
          Alert.info(`You are not authorized to access ${location.pathname}.`);
        } else {
          dispatch.adminSession.logout();
        }
      }
    }, [userRole, location.pathname, navigate, user]);

    // Return early if user is not an ORGANIZATION_IT_ADMIN
    if (userRole !== RoleNameEnum.ORGANIZATION_IT_ADMIN) {
      return React.createElement(Component, props as any as OwnProps);
    }

    // Only for ORGANIZATION_IT_ADMIN, check if the path is allowed
    return isAllowedPathForItAdmin(location.pathname)

      ? React.createElement(Component, props as any as OwnProps)
      : null;
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  return connect(mapState)(Redirect);
}
export const AdminIsOrganizationAdminOnlyGuard = createAdminHasRoleGuard(
  RoleNameEnum.ORGANIZATION_IT_ADMIN,
  RoleNameEnum.ORGANIZATION_ADMIN,
);

/**
 * Guard that allows access if user is RESIDE_ADMIN or organization doesn't have SSO config.
 * Used to block user management routes for non-admin users in SSO organizations.
 */
export const AdminCanManageUsersGuard = createRouteGuard({
  authenticatedSelector: state => {
    const isResideAdmin = select.adminSession.isResideAdmin(state);
    const userOrganizationHasSsoConfig =
      select.adminSession.userOrganizationHasSsoConfig(state);
    // Allow if RESIDE_ADMIN or organization doesn't have SSO config
    return isResideAdmin || !userOrganizationHasSsoConfig;
  },
  redirectPath: "/admin/dashboard",
});
